import log from 'loglevel'
import {
  Form,
  Labeled,
  Loading,
  SimpleShowLayout,
  TextField,
  Title,
  useGetIdentity,
  useGetOne,
  useLogout,
  useNotify,
  usePermissions,
  useRecordContext,
  type UserIdentity,
  useTranslate,
  useUpdate
} from 'react-admin'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery
} from '@mui/material'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { TextInput } from 'components/Util'
import React, { useEffect, useState } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import {
  endsWith,
  find,
  isEmpty,
  isEqual,
  isNil,
  join,
  map,
  replace,
  some,
  startsWith
} from 'lodash/fp'
import {
  type PartnerUser,
  type UserAttribute,
  UserStatus
} from '@mzc-watchover/adminapp-appsync'
import { assign } from 'lodash'
import { type CognitoUser } from 'amazon-cognito-identity-js'
import { useNavigate } from 'react-router-dom'
import { ScheduleInput } from 'components/Schedule'
// import { AnnounceUser } from 'components/Announce'
import { addMonths, lastDayOfMonth } from 'date-fns/fp'

log.setLevel(log.levels.DEBUG)
// const env = process.env['REACT_APP_ENV'] as string
type ShowParams = {
  stop: string
  phone: string
  email: string
  family: {
    name: string
    nameKana: string
    email: string
    phone: string
    postalCode: string
  }
  schedules: string[]
}
type PartnerUserSub = Partial<PartnerUser> & {
  id: string
  contractor: { name?: string; address?: string }
}
const SubmitButton: React.FC<{
  record: PartnerUserSub
  setRecord: (data: PartnerUserSub) => void
  schedules: string[] | null | undefined
  setSubmit: (submit: boolean) => void
}> = ({ record, setRecord, schedules, setSubmit }) => {
  const [enable, setEnable] = React.useState(false)
  const [data, setData] = React.useState({})
  const { getValues } = useFormContext()
  const translate = useTranslate()
  const { errors } = useFormState()
  const [update] = useUpdate(undefined, undefined, {
    onSettled: (
      data: (PartnerUserSub & { editUser?: boolean }) | undefined
    ) => {
      if (!isNil(data)) setRecord(data)
    }
  })
  const { stop, phone, family, email } = getValues() as ShowParams
  const {
    stop: currentStop,
    phone: currentPhone,
    email: currentEmail,
    families,
    start,
    status
  } = record
  const [currentSchedules, setCurrentSchedules] = React.useState<
    string[] | null | undefined
  >(record.schedules)
  const currentFamily =
    !isNil(families) && !isNil(families[0]) ? families[0] : undefined
  const onSubmit = (): void => {
    const { id } = record
    setEnable(false)
    // const { schedules } = getValues()
    // assign(data, { schedules })
    setCurrentSchedules(schedules)
    update('users', {
      id,
      data: { ...data, editUser: true },
      previousData: record
    }).catch(log.error)
    setSubmit(true)
  }
  React.useEffect(() => {
    if (isEmpty(errors)) {
      const data = {}
      if (!isNil(stop) && stop !== currentStop)
        assign(data, {
          stop,
          status: isNil(start)
            ? status
            : !isNil(stop)
            ? UserStatus.stop
            : UserStatus.inUse
        })
      if (!isNil(phone) && phone !== currentPhone) {
        assign(data, { phone })
      }
      if (!isNil(email) && email !== currentEmail) {
        assign(data, { email })
      }
      if (!isNil(currentFamily)) {
        const {
          name: currentFamilyName,
          nameKana: currentFamilyNameKana,
          phone: currentFamilyPhone,
          postalCode: currentFamilyPostalCode,
          email: currentFamilyEmail
        } = currentFamily
        const familyData = JSON.parse(JSON.stringify(currentFamily))
        if (!isNil(family.name) && family.name !== currentFamilyName) {
          assign(familyData, { name: family.name })
        }
        if (
          !isNil(family.nameKana) &&
          family.nameKana !== currentFamilyNameKana
        ) {
          assign(familyData, { nameKana: family.nameKana })
        }
        if (!isNil(family.phone) && family.phone !== currentFamilyPhone) {
          assign(familyData, { phone: family.phone })
        }
        if (
          !isNil(family.postalCode) &&
          family.postalCode !== currentFamilyPostalCode
        ) {
          assign(familyData, { postalCode: family.postalCode })
        }
        if (!isNil(family.email) && family.email !== currentFamilyEmail) {
          assign(familyData, { email: family.email })
        }
        if (!isEmpty(familyData) && !isEqual(familyData)(currentFamily)) {
          assign(data, { families: [familyData] })
        }
      }
      // log.debug({
      //   schedules,
      //   currentSchedules,
      //   equal: isEqual(isNil(schedules) ? [] : schedules)(
      //     isNil(currentSchedules) ? [] : currentSchedules
      //   )
      // })
      if (
        !isEqual(isNil(schedules) ? [] : schedules)(
          isNil(currentSchedules) ? [] : currentSchedules
        )
      ) {
        assign(data, { schedules: isNil(schedules) ? [] : schedules })
      }
      // log.debug({ data })
      setEnable(!isEmpty(data))
      setData(data)
    }
  }, [
    errors,
    stop,
    phone,
    email,
    family.name,
    family.nameKana,
    family.phone,
    family.postalCode,
    family.email,
    schedules,
    record
  ])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      // fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate('resources.users.show.parsonal.buttons.submit')}
      </Typography>
    </Button>
  )
}
const ConfirmButton: React.FC<{
  record: PartnerUserSub
  setConfirm: (confirm: boolean) => void
  schedules: string[] | null | undefined
  setData: (
    data: Partial<PartnerUserSub & { schedules: string[] | null | undefined }>
  ) => void
}> = ({ record, setConfirm, schedules, setData }) => {
  const [enable, setEnable] = React.useState(false)
  const { getValues } = useFormContext()
  const translate = useTranslate()
  const { errors } = useFormState()
  const { stop, phone, family, email } = getValues() as ShowParams
  const {
    stop: currentStop,
    phone: currentPhone,
    email: currentEmail,
    families,
    start,
    status
  } = record
  const [currentSchedules, setCurrentSchedules] = React.useState<
    string[] | null | undefined
  >(record.schedules)
  const currentFamily =
    !isNil(families) && !isNil(families[0]) ? families[0] : undefined
  const onSubmit = (): void => {
    setEnable(false)
    // const { schedules } = getValues()
    // assign(data, { schedules })
    setCurrentSchedules(schedules)
    setConfirm(true)
  }
  React.useEffect(() => {
    if (isEmpty(errors)) {
      const data = {}
      if (!isNil(stop) && stop !== currentStop)
        assign(data, {
          stop,
          status: isNil(start)
            ? status
            : !isNil(stop)
            ? UserStatus.stop
            : UserStatus.inUse
        })
      if (!isNil(phone) && phone !== currentPhone) {
        assign(data, { phone })
      }
      if (!isNil(email) && email !== currentEmail) {
        assign(data, { email })
      }
      if (!isNil(currentFamily)) {
        const {
          name: currentFamilyName,
          nameKana: currentFamilyNameKana,
          phone: currentFamilyPhone,
          postalCode: currentFamilyPostalCode,
          email: currentFamilyEmail
        } = currentFamily
        // const familyData = JSON.parse(JSON.stringify(currentFamily))
        const familyData = {}
        if (!isNil(family.name) && family.name !== currentFamilyName) {
          assign(familyData, { name: family.name })
        }
        if (
          !isNil(family.nameKana) &&
          family.nameKana !== currentFamilyNameKana
        ) {
          assign(familyData, { nameKana: family.nameKana })
        }
        if (!isNil(family.phone) && family.phone !== currentFamilyPhone) {
          assign(familyData, { phone: family.phone })
        }
        if (
          !isNil(family.postalCode) &&
          family.postalCode !== currentFamilyPostalCode
        ) {
          assign(familyData, { postalCode: family.postalCode })
        }
        if (!isNil(family.email) && family.email !== currentFamilyEmail) {
          assign(familyData, { email: family.email })
        }
        if (!isEmpty(familyData) && !isEqual(familyData)(currentFamily)) {
          assign(data, { families: [familyData] })
        }
      }
      // log.debug({
      //   schedules,
      //   currentSchedules,
      //   equal: isEqual(isNil(schedules) ? [] : schedules)(
      //     isNil(currentSchedules) ? [] : currentSchedules
      //   )
      // })
      if (
        !isEqual(isNil(schedules) ? [] : schedules)(
          isNil(currentSchedules) ? [] : currentSchedules
        )
      ) {
        assign(data, { schedules: isNil(schedules) ? [] : schedules })
      }
      // log.debug({ data })
      setEnable(!isEmpty(data))
      setData(data)
    }
  }, [
    errors,
    stop,
    phone,
    email,
    family.name,
    family.nameKana,
    family.phone,
    family.postalCode,
    family.email,
    schedules,
    record
  ])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      // fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate('resources.users.show.buttons.confirm')}
      </Typography>
    </Button>
  )
}
const UserTextField: React.FC<{
  source: string
  label: boolean
  ownLabel?: string
}> = ({ source, ownLabel }) => {
  const record = useRecordContext<PartnerUser>()
  const { start, analytics } = record
  return (
    <Labeled
      label={
        !isNil(ownLabel)
          ? ownLabel
          : isNil(analytics) || !startsWith('solar')(analytics)
          ? `resources.users.parsonalFields.${source}`
          : `resources.users.solarFields.${source}`
      }
      sx={{ fontVariant: 'body1' }}>
      {source === 'start' && (isNil(start) || isEmpty(start)) ? (
        <Typography variant="body1">利用開始前</Typography>
      ) : (
        <TextField variant="body1" source={source} />
      )}
    </Labeled>
  )
}
// const UserReferenceField: React.FC<{
//   source: string
//   label: boolean
//   ownLabel?: string
//   reference: string
//   children: JSX.Element
// }> = ({ source, ownLabel, reference, children }) => {
//   return (
//     <Labeled
//       label={!isNil(ownLabel) ? ownLabel : `resources.users.fields.${source}`}
//       sx={{ fontVariant: 'body1' }}>
//       <ReferenceField
//         reference={reference}
//         source={source}
//         sx={{ fontVariant: 'body1' }}>
//         {children}
//       </ReferenceField>
//     </Labeled>
//   )
// }
const ParsonalUserRecordShow: React.FC<{
  record: PartnerUserSub
}> = ({ record }) => {
  const translate = useTranslate()
  const { stop, end, status, analytics, contractor } = record
  const statusLabel = {
    [UserStatus.inUse]: '提供中',
    [UserStatus.switching]: '提供中',
    [UserStatus.switchingInWaitingResult]: '提供中',
    [UserStatus.switchingInWaitingStart]: '提供中',
    [UserStatus.beforeStart]: '提供準備中',
    [UserStatus.waitingResult]: '提供準備中',
    [UserStatus.waitingStart]: '提供準備中',
    [UserStatus.stop]: '解約予定',
    [UserStatus.end]: '解約済み'
  }
  const showRecord = {
    ...record,
    idItem: undefined,
    status: statusLabel[status as keyof typeof statusLabel]
  }
  return (
    <Grid container spacing={2} sx={{ marginTop: '1em' }}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Typography variant="h5">
          {translate('resources.users.show.parsonal.useageStatus.title')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '-1em'
        }}>
        <picture>
          <img src="/bar.png" alt="bar" />
        </picture>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <SimpleShowLayout record={showRecord}>
          <UserTextField source="status" label={false} />
          <UserTextField source="start" label={false} />
          {!isNil(stop) && !isEmpty(stop) && (
            <UserTextField source="stop" label={false} />
          )}
          {!isNil(end) && !isEmpty(end) && (
            <UserTextField source="end" label={false} />
          )}
        </SimpleShowLayout>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <EditSchedule data={record as PartnerUser} />
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Typography variant="h5">
          {translate('resources.users.show.parsonal.record.title')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '-1em'
        }}>
        <picture>
          <img src="/bar.png" alt="bar" />
        </picture>
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <SimpleShowLayout record={showRecord}>
          <UserTextField source="name" label={false} />
          {/* <UserReferenceField
            reference="organizations"
            source="product"
            label={false}>
            <FunctionField
              source="contactOrganization"
              variant="body1"
              render={(record: Product) => {
                const { contactOrganization } = record
                return `${contactOrganization as string}`
              }}
            />
          </UserReferenceField> */}
          <UserTextField source="address" label={false} />
          {(isNil(analytics) || !startsWith('solar')(analytics)) && (
            <Grid container>
              <Grid item xs={8}>
                <UserTextField source="place" label={false} />
              </Grid>
              <Grid item xs={4}>
                <UserTextField source="placeNumber" label={false} />
              </Grid>
            </Grid>
          )}
          <UserTextField source="phone" label={false} />
          <UserTextField source="email" label={false} />
          {!isNil(contractor) && !isEmpty(contractor.name) && (
            <Grid container>
              <Grid item xs={12}>
                <UserTextField source="contractor.name" label={false} />
              </Grid>
              <Grid item xs={12}>
                <UserTextField source="contractor.address" label={false} />
              </Grid>
            </Grid>
          )}
          <UserTextField source="families.0.name" label={false} />
          <UserTextField source="families.0.nameKana" label={false} />
          <UserTextField source="families.0.phone" label={false} />
          <UserTextField source="families.0.email" label={false} />
          <UserTextField source="families.0.postalCode" label={false} />
        </SimpleShowLayout>
      </Grid>
    </Grid>
  )
}
const CustomTextField: React.FC<{
  source: string
  value?: string
  children?: string
}> = (props) => {
  const { source, value, children } = props
  const translate = useTranslate()
  return (
    <Labeled
      label={translate(`resources.users.parsonalFields.${source}`)}
      sx={{ fontVariant: 'body1' }}>
      <Typography variant="body1">{value ?? children ?? ''}</Typography>
    </Labeled>
  )
}
const ParsonalUserEditShow: React.FC<{
  record: Partial<PartnerUserSub>
}> = ({ record }) => {
  const { phone, families, schedules } = record
  const family = !isNil(families) ? families[0] : undefined
  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: '2em'
        }}>
        <Grid item xs={10}>
          <Grid item xs={12} sx={{ my: '2em' }}>
            <Typography variant="h6" whiteSpace="pre-wrap">
              {`下記の内容で登録します。よろしければ「送信」を押してください。\n※なお、ご入力内容の誤りに起因して生じた損害または不利益について、当社は責任を負いかねます。`}
            </Typography>
          </Grid>
          {!isEmpty(phone) && (
            <Grid item xs={12}>
              <CustomTextField source="phone">{phone}</CustomTextField>
            </Grid>
          )}
          {!isNil(family) ? (
            <>
              {!isEmpty(family.name) && (
                <Grid item xs={12}>
                  <CustomTextField
                    source="families.0.name"
                    value={family.name}
                  />
                </Grid>
              )}
              {!isEmpty(family.nameKana) && (
                <Grid item xs={12}>
                  <CustomTextField
                    source="families.0.nameKana"
                    value={family.nameKana}
                  />
                </Grid>
              )}
              {!isEmpty(family.phone) && (
                <Grid item xs={12}>
                  <CustomTextField
                    source="families.0.phone"
                    value={family.phone}
                  />
                </Grid>
              )}
              {!isEmpty(family.email) && (
                <Grid item xs={12}>
                  <CustomTextField
                    source="families.0.email"
                    value={family.email}
                  />
                </Grid>
              )}
              {!isEmpty(family.postalCode) && (
                <Grid item xs={12}>
                  <CustomTextField
                    source="families.0.postalCode"
                    value={family.postalCode}
                  />
                </Grid>
              )}
            </>
          ) : (
            <></>
          )}
          {!isEmpty(schedules) ? (
            <>
              <Typography sx={{ whiteSpace: 'pre-line' }} variant="body1">
                {join('\n')(
                  map((schedule: string) =>
                    replace(/(\d{4})-(\d{2})-(\d{2})/, '$1年$2月$3日')(schedule)
                  )(schedules)
                )}
              </Typography>
            </>
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
const ParsonalUserEditComplete: React.FC = () => {
  return (
    <Grid container spacing={2}>
      <Grid
        item
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: '1em'
        }}>
        <Grid item xs={10}>
          <Grid item>
            <Typography
              sx={{
                lineHeight: '2em',
                whiteSpace: 'pre-line'
              }}>{`お客様の情報を変更いたしました。
            変更内容をメールアドレスに送信いたしましたので、ご確認くださいますようお願い申し上げます。`}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
const ParsonalUserShowForm: React.FC<{
  data: PartnerUser
}> = ({ data }) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { isLoading: isLoadingPermissions } = usePermissions()
  const [record, setRecord] = React.useState<PartnerUserSub | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  React.useEffect(() => {
    setRecord({ ...data, contractor: data.contractor ?? '' })
  }, [data])
  if (isLoadingPermissions) return <Loading />
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', marginBottom: '3em' }}>
      <Box sx={{ width: isDesktop ? '1280px' : '100%' }}>
        {!isNil(record) && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ marginTop: '0.1em' }}>
            <Grid item xs={12}>
              {!isNil(record) ? (
                <ParsonalUserRecordShow record={record} />
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        )}
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ marginTop: '0.1em', paddingBottom: '1em' }}>
          <Grid item xs={isDesktop ? 4 : 10}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              sx={{ borderRadius: '10px' }}
              fullWidth
              onClick={() => {
                setOpen(true)
              }}>
              <Typography variant="button">
                {translate('resources.users.show.parsonal.record.edit')}
              </Typography>
            </Button>
          </Grid>
        </Grid>
        <Dialog
          open={open}
          onClose={() => {
            setOpen(false)
          }}>
          <DialogContent>
            <ParsonalUserEditForm data={data} setOpen={setOpen} />
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  )
}
export const ParsonalUserEditForm: React.FC<{
  data: PartnerUser
  setOpen: (open: boolean) => void
}> = ({ data, setOpen }) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { isLoading: isLoadingPermissions } = usePermissions()
  const [record, setRecord] = React.useState<PartnerUserSub | null>(null)
  const [confirm, setConfirm] = useState<boolean>(false)
  const [submit, setSubmit] = useState<boolean>(false)
  const [schedules, setSchedules] = React.useState<string[] | null | undefined>(
    record?.schedules
  )
  const [editRecords, setEditRecords] = useState<
    Partial<PartnerUserSub & { schedules: string[] | null | undefined }>
  >({})
  const family =
    !isNil(record) && !isNil(record.families) ? record.families[0] : undefined
  const schema = z.object({
    stop: z.string().nullable(),
    phone: z
      .string()
      .min(11, { message: 'too small' })
      .max(11, { message: 'too large' }),
    email: z.string().email({ message: 'invalid syntax' }),
    family: z.object({
      name: z.string().min(1),
      nameKana: z.string().min(1),
      phone: z
        .string()
        .min(11, { message: 'too small' })
        .max(11, { message: 'too large' }),
      email: z.string().email({ message: 'invalid syntax' }),
      postalCode: z
        .string()
        .min(7, { message: 'too small' })
        .max(7, { message: 'too large' })
    }),
    schedules: z.array(z.string()).optional()
  })
  React.useEffect(() => {
    setRecord({ ...data, contractor: data.contractor ?? '' })
    if (!isNil(record)) setSchedules(record.schedules)
  }, [data])
  if (isLoadingPermissions) return <Loading />
  return (
    <Form
      resolver={zodResolver(schema)}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={
        isNil(record) || isNil(family)
          ? {}
          : {
              stop: isEmpty(record.stop) ? null : record.stop,
              phone: record.phone,
              email: record.email,
              family,
              schedules
            }
      }>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: '2em'
        }}>
        <Typography variant="h5">
          {translate('resources.users.show.parsonal.record.edit')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '-2em'
        }}>
        <picture>
          <img src="/bar.png" alt="bar" />
        </picture>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: isDesktop ? '1280px' : '100%' }}>
          {!confirm ? (
            !isNil(record) && (
              <>
                <Box sx={{ marginTop: '1em' }}>
                  <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={isDesktop ? 8 : 12}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="phone"
                          placeholder="09012345678"
                          fields="resources.users.show.fields"
                        />
                      </Grid>
                    </Grid>
                    {/* <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: '0.1em' }}>
                    <Grid
                      item
                      xs={4}
                      sx={{
                        marginBottom: '0.2em'
                      }}>
                      <TextInput
                        source="email"
                        placeholder="chuden.hanako@chuden.co.jp"
                        fields="resources.users.show.fields"
                      />
                    </Grid>
                  </Grid> */}
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={isDesktop ? 8 : 12}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="family.name"
                          placeholder="中電太郎"
                          fields="resources.users.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={isDesktop ? 8 : 12}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="family.nameKana"
                          placeholder="チュウデンタロウ"
                          fields="resources.users.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={isDesktop ? 8 : 12}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="family.phone"
                          placeholder="09012345678"
                          fields="resources.users.show.fields"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={isDesktop ? 8 : 12}
                        sx={{
                          marginBottom: '0.2em'
                        }}>
                        <TextInput
                          source="family.email"
                          placeholder="chuden.taro@chuden.co.jp"
                          fields="resources.users.show.fields"
                        />
                      </Grid>
                    </Grid>
                    {!isNil(family) && !isNil(family.postalCode) && (
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={isDesktop ? 8 : 12}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="family.postalCode"
                            placeholder="0123456"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Box>
                <Box sx={{ marginTop: '1em', marginBottom: '2em' }}>
                  <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={isDesktop ? 8 : 12}
                        display="flex"
                        justifyContent="space-between">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="large"
                          sx={{ borderRadius: '10px' }}
                          onClick={() => {
                            setOpen(false)
                          }}>
                          {translate(
                            'resources.users.show.parsonal.buttons.cancel'
                          )}
                        </Button>
                        <ConfirmButton
                          record={record}
                          setConfirm={setConfirm}
                          schedules={schedules}
                          setData={setEditRecords}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </>
            )
          ) : !submit ? (
            !isNil(record) && (
              <>
                <ParsonalUserEditShow record={editRecords} />
                <Box sx={{ marginTop: '1em', marginBottom: '2em' }}>
                  <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={isDesktop ? 8 : 12}
                        display="flex"
                        justifyContent="space-between">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="large"
                          sx={{ borderRadius: '10px' }}
                          onClick={() => {
                            setConfirm(false)
                          }}>
                          {translate(
                            'resources.users.show.parsonal.buttons.cancel'
                          )}
                        </Button>
                        <SubmitButton
                          record={record}
                          setRecord={setRecord}
                          schedules={schedules}
                          setSubmit={setSubmit}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </>
            )
          ) : (
            <Box display="flex" justifyContent="center" sx={{ marginY: '1em' }}>
              <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  sx={{ marginTop: '0.1em' }}>
                  <Grid item>
                    <Grid item>
                      <ParsonalUserEditComplete />
                    </Grid>
                    <Grid item display="flex" justifyContent="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        sx={{ borderRadius: '10px' }}
                        onClick={() => {
                          setOpen(false)
                        }}>
                        {translate(
                          'resources.users.show.parsonal.buttons.close'
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Form>
  )
}
const ScheduleButton: React.FC<{
  source: string
  label: string
  record: PartnerUserSub | null
  setRecord: (data: PartnerUserSub) => void
}> = ({ source, label, record, setRecord }) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const [schedules, setSchedules] = React.useState<string[] | null | undefined>(
    record?.schedules
  )
  const [open, setOpen] = React.useState<boolean>(false)
  const [confirm, setConfirm] = React.useState<boolean>(false)
  const [submit, setSubmit] = React.useState<boolean>(false)
  const { getValues } = useFormContext()
  const current = getValues()
  const onClick = (): void => {
    setOpen(true)
  }
  const handleClose = (): void => {
    setOpen(false)
    setSubmit(false)
    setConfirm(false)
  }
  const handleSet = (): void => {
    setConfirm(true)
  }
  return (
    <>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        spacing={2}
        xs={isDesktop ? 9 : 10}
        sx={{
          marginTop: '0.1em',
          paddingBottom: '1em',
          paddingLeft: isDesktop ? '1em' : '0.5em'
        }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onClick}
          fullWidth
          disabled={isNil(record)}
          sx={{
            borderRadius: '10px'
            // backgroundColor: '#4472c4'
          }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {translate(label)}
          </Typography>
        </Button>
      </Grid>
      <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
        <DialogContent>
          {!confirm ? (
            <ScheduleInput
              source={source}
              current={current[source]}
              onSet={handleSet}
              onCancel={handleClose}
              setSchedules={setSchedules}
              maxDate={lastDayOfMonth(addMonths(1)(Date.now()))}
            />
          ) : !submit ? (
            !isNil(record) && (
              <>
                <ParsonalUserEditShow
                  record={{ schedules: schedules ?? null }}
                />
                <Box sx={{ marginTop: '1em', marginBottom: '2em' }}>
                  <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      sx={{ marginTop: '0.1em' }}>
                      <Grid
                        item
                        xs={8}
                        display="flex"
                        justifyContent="space-between">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="large"
                          sx={{ borderRadius: '10px' }}
                          onClick={() => {
                            setConfirm(false)
                            setSchedules(record?.schedules)
                          }}>
                          {translate(
                            'resources.users.show.parsonal.buttons.cancel'
                          )}
                        </Button>
                        <SubmitButton
                          record={record}
                          setRecord={setRecord}
                          schedules={schedules}
                          setSubmit={setSubmit}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>{' '}
              </>
            )
          ) : (
            <Box display="flex" justifyContent="center" sx={{ marginY: '1em' }}>
              <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  sx={{ marginTop: '0.1em' }}>
                  <Grid item>
                    <Grid item>
                      <ParsonalUserEditComplete />
                    </Grid>
                    <Grid item display="flex" justifyContent="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        sx={{ borderRadius: '10px' }}
                        onClick={handleClose}>
                        {translate(
                          'resources.users.show.parsonal.buttons.close'
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
const EditSchedule: React.FC<{
  data: PartnerUser
}> = ({ data }) => {
  const translate = useTranslate()
  // const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { isLoading: isLoadingPermissions } = usePermissions()
  const [record, setRecord] = React.useState<PartnerUserSub | null>(null)
  const family =
    !isNil(record) && !isNil(record.families) ? record.families[0] : undefined
  const schema = z.object({
    stop: z.string().nullable(),
    phone: z
      .string()
      .min(11, { message: 'too small' })
      .max(11, { message: 'too large' }),
    email: z.string().email({ message: 'invalid syntax' }),
    family: z.object({
      name: z.string().min(1),
      nameKana: z.string().min(1),
      phone: z
        .string()
        .min(11, { message: 'too small' })
        .max(11, { message: 'too large' }),
      email: z.string().email({ message: 'invalid syntax' }),
      postalCode: z
        .string()
        .min(7, { message: 'too small' })
        .max(7, { message: 'too large' })
    }),
    schedules: z.array(z.string()).optional()
  })
  React.useEffect(() => {
    setRecord({ ...data, contractor: data.contractor ?? '' })
  }, [data])
  if (isLoadingPermissions) return <Loading />
  return (
    <Form
      resolver={zodResolver(schema)}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={
        isNil(record) || isNil(family)
          ? {}
          : {
              stop: isEmpty(record.stop) ? null : record.stop,
              phone: record.phone,
              family,
              schedules: record.schedules
            }
      }>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '3em',
          marginBottom: '3em'
        }}>
        {/* <Box sx={{ width: isDesktop ? '1280px' : '100%' }}> */}
        <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
          {/* <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={2}> */}
          <Grid item xs={12}>
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              <Typography variant="h5">
                {translate('resources.users.show.parsonal.schedules.title')}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              <picture>
                <img src="/bar.png" alt="bar" />
              </picture>
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingY: '1em'
              }}>
              <Typography variant="h6" whiteSpace={'pre-line'}>
                {`外出予定がある場合は、下記より登録をお願いします。\n登録された期間中の安否確認は実施しません。`}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              <ScheduleButton
                source="schedules"
                label="resources.users.show.parsonal.schedules.edit"
                record={
                  !isNil(record)
                    ? { id: record.id, contractor: record.contractor }
                    : null
                }
                setRecord={setRecord}
              />
            </Grid>
          </Grid>
          {/* </Grid> */}
        </Box>
        {/* </Box> */}
      </Box>
    </Form>
  )
}
export const ParsonalUserMyPage: React.FC<{
  userId: string
}> = ({ userId, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { data, isLoading } = useGetOne<PartnerUser>('users', {
    id: userId
  })
  if (isLoading) return <Loading />
  return (
    !isNil(data) && (
      <Grid
        container
        sx={{
          width: isDesktop ? '1280px' : '100%'
        }}>
        <Grid display="flex" justifyContent="center">
          <Grid
            sx={{ marginLeft: '1em', marginRight: '1em' }}
            xs={isDesktop ? 8 : 12}>
            {/* <Grid item xs={12}>
              <AnnounceUser partner={data.partner} />
            </Grid> */}
            <Grid item xs={12}>
              <ParsonalUserShowForm {...props} data={data} />
              <ParsonalUserPaymentEdit data={data} />
              <ParsonalUserContacts />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  )
}
const DirectDebitLink: React.FC<{ id: string; partnerId: string }> = ({
  id
}) => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const onClick = (): void => {
    navigate(
      `${window.location.pathname}${
        endsWith('/')(window.location.pathname) ? '' : '/'
      }bankaccount/${id}`
    )
  }
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      onClick={onClick}>
      <Typography variant="button">
        {translate('resources.users.show.buttons.directDebit')}
      </Typography>
    </Button>
  )
}
const CreditCardEditLink: React.FC<{ id: string }> = ({ id }) => {
  const translate = useTranslate()
  const [cardUrl, setCardUrl] = useState<string | undefined>(undefined)
  const { isLoading, data } = useGetOne('gmopg/credit-url', { id })
  const onClick = (): void => {
    if (!isNil(cardUrl)) window.location.href = cardUrl
  }
  useEffect(() => {
    if (!isNil(data)) {
      const { url } = data
      setCardUrl(url)
    }
  }, [data])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      disabled={isLoading}
      onClick={onClick}>
      <Typography variant="button">
        {isLoading ? (
          <CircularProgress />
        ) : (
          translate('resources.users.show.buttons.creditCard')
        )}
      </Typography>
    </Button>
  )
}
const ContactLink: React.FC = () => {
  const translate = useTranslate()
  const onClick = (): void => {
    location.href =
      'https://app.fb.miraiz-connect.jp/019e719d-361f-7331-bbf1-d4956cbc484b'
  }
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      onClick={onClick}>
      <Typography variant="button">
        {translate('resources.users.show.parsonal.contacts.button')}
      </Typography>
    </Button>
  )
}
const ParsonalUserPaymentEdit: React.FC<{ data: PartnerUser }> = ({ data }) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { attributes, id, partner } = data
  if (isNil(attributes)) {
    log.error(`attributes is null`)
    return <></>
  }
  const useDirectDebit = some(
    (item: UserAttribute) => item.key === 'directDebit'
  )(attributes)
  const gmoMemberItem = find(
    (item: UserAttribute) => item.key === 'gmoMemberId'
  )(attributes)
  if (!useDirectDebit && isNil(gmoMemberItem)) {
    log.error(`gmo item is not exist`)
    return <></>
  }
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '3em',
        marginBottom: '3em'
      }}>
      <Box sx={{ width: isDesktop ? '1280px' : '100%' }}>
        <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}>
            <Typography variant="h5">
              {translate('resources.users.show.parsonal.payment.title')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}>
            <picture>
              <img src="/bar.png" alt="bar" />
            </picture>
          </Grid>
          <Grid
            item
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingY: '1em'
            }}>
            <Typography variant="h6" whiteSpace={'pre-line'}>
              {translate('resources.users.show.parsonal.payment.edit')}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ marginTop: '0.1em', paddingBottom: '1em' }}>
            <Grid item xs={isDesktop ? 4 : 10}>
              {useDirectDebit ? (
                <DirectDebitLink id={id} partnerId={partner} />
              ) : !isNil(gmoMemberItem) ? (
                <CreditCardEditLink id={id} />
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}
const ParsonalUserContacts: React.FC = () => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '3em',
        marginBottom: '3em'
      }}>
      <Box sx={{ width: isDesktop ? '1280px' : '100%' }}>
        <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}>
            <Typography variant="h5">
              {translate('resources.users.show.parsonal.contacts.title')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}>
            <picture>
              <img src="/bar.png" alt="bar" />
            </picture>
          </Grid>
          <Grid
            item
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingY: '1em'
            }}>
            <Typography variant="h6" whiteSpace={'pre-line'}>
              {translate('resources.users.show.parsonal.contacts.description')}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ marginTop: '0.1em', paddingBottom: '1em' }}>
            <Grid item xs={isDesktop ? 4 : 10}>
              <ContactLink />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}
export const ParsonalUser: React.FC = () => {
  const { isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const logout = useLogout()
  const { data: identity, isLoading: isLoadingCognito } = useGetIdentity()
  if (isLoadingCognito) return <Loading />
  if (isNil(identity)) {
    notify('resources.dashboard.notification.cannotUse', { type: 'error' })
    logout()
      .then(() => {})
      .catch(log.error)
    return <></>
  }
  const { cognitoUser } = identity as UserIdentity & {
    cognitoUser: CognitoUser
  }
  const session = cognitoUser.getSignInUserSession()
  if (isNil(session)) {
    notify('resources.dashboard.notification.cannotUse', { type: 'error' })
    logout()
      .then(() => {})
      .catch(log.error)
    return <></>
  }
  const idToken = session.getIdToken()
  const { payload } = idToken
  const userId = payload['custom:id']
  if (isLoadingCognito || isLoadingPermissions) return <Loading />
  return (
    <>
      <Grid container justifyContent="center">
        <Title title={`ご利用者様マイページ`} />
        <ParsonalUserMyPage userId={userId} />
      </Grid>
      <Footer />
    </>
  )
}
const Footer: React.FC<{ noFooter?: boolean }> = ({ noFooter }) => {
  // const termsUrl = !isNil(data) ? `${entryBaseUrl}/agreement/${data.product}` : undefined
  return !isNil(noFooter) && noFooter ? (
    <></>
  ) : (
    <footer>
      <div className="footerwrapper_3">
        <div className="footerInner">
          <ul className="linkUl">
            <li>
              <a href="https://miraiz-connect.co.jp/" target="_blank">
                運営会社
              </a>
            </li>
            <li>
              <a
                href="https://miraiz-connect.co.jp/privacy/website/"
                target="_blank">
                プライバシーポリシー
              </a>
            </li>
            {
              <li>
                <a
                  href={'https://miraiz-connect.co.jp/privacy/website/'}
                  target="_blank">
                  利用規約
                </a>
              </li>
            }
          </ul>
          <div className="info_box">
            <p className="logo">
              <img src="/common/img/logo.png" alt="中部電力ミライズコネクト" />
            </p>
            <div className="info">
              <p className="adr">愛知県名古屋市中区栄4丁目5番3号</p>
              <p className="ask"></p>
            </div>
          </div>
        </div>
      </div>
      <center>
        <p id="copy">
          (C)Chubu Electric Power Miraiz Connect, <br className="sp" />
          Inc.All Rights Reserved
        </p>
      </center>
    </footer>
  )
}
